function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import _ from 'underscore';
import jsonpath from 'jsonpath';
import { clone } from 'ramda';
import { setValue } from '@truqc/content-path';
import { constantsRegistry } from '../../constants/tableConstantsFactory';
import detailReducerFactory from './detailReducerFactory';
const removeDestroyedEntity = (state, incomingIds) => {
  let ids = incomingIds;
  if (!_.isArray(ids)) {
    ids = [ids];
  }
  let newList = state.list;
  ids.forEach(id => {
    newList = _.without(newList, _.findWhere(newList, {
      id
    }));
  });
  return {
    list: newList
  };
};

// REDUCER METHODS

const setJSONPath = (path, previousData, newData) => {
  const clonedData = clone(previousData);
  const jsonPath = `$/${path}`.split('/').map(part => part.replace(/\//g, '')).join('.');
  jsonpath.value(clonedData, jsonPath, newData);
  return clonedData;
};

// We diverge between using content-path and jsonpath at this point
// This was done becuase we have existing code that constructs content paths
// with array subscripts (no longer supported in content-path) in our non-RB
// components.  Instead of muddying content-path we decided it was best
// to use jsonpath here instead.  Ideally we want to not have to do this conversion
// and have our non-RB components just provide valid jsonpaths.
// Pivotal Ticket: https://www.pivotaltracker.com/story/show/173288370
const safeSet = (path, previousData, newData) => {
  const isJSONPath = path.includes('[') || path.includes(']');
  if (isJSONPath) {
    return setJSONPath(path, previousData, newData);
  }
  return setValue(path, previousData, newData);
};
const changeQuietly = (state, action) => {
  const {
    data,
    path,
    syncOriginal
  } = action;
  const active = safeSet(path, state.active, data);
  if (!syncOriginal) {
    return _objectSpread(_objectSpread({}, state), {}, {
      active
    });
  }
  return _objectSpread(_objectSpread({}, state), {}, {
    active,
    originalActive: safeSet(path, state.originalActive || {}, data)
  });
};
const change = (state, action) => {
  const newState = changeQuietly(state, action);
  return _objectSpread(_objectSpread({}, newState), {}, {
    pendingChanges: true
  });
};
const operationPending = state => _objectSpread(_objectSpread({}, state), {}, {
  pendingOperation: true
});
const operationComplete = state => _objectSpread(_objectSpread({}, state), {}, {
  pendingOperation: false
});
const create = (state, action) => {
  const active = _objectSpread(_objectSpread({
    id: action.id
  }, action.defaultData), {}, {
    isNew: true
  });
  return _objectSpread(_objectSpread({}, state), {}, {
    active,
    originalActive: clone(active),
    error: null,
    loading: false,
    pendingChanges: true
  });
};

/**
 * @method createFail
 * clear state of entity that failed to save during creation.
 */
const createFail = (state, action) => _objectSpread(_objectSpread({}, state), {}, {
  error: action.error ? action.error : {
    message: 'failed creating'
  },
  loading: false,
  pendingChanges: false,
  active: {},
  originalActive: {}
});
const destroy = state => _objectSpread(_objectSpread({}, state), {}, {
  error: null,
  loading: true,
  pendingChanges: true
});
const destroyFail = (state, action) => _objectSpread(_objectSpread({}, state), {}, {
  error: action.error || {
    message: 'failed destroying entity'
  },
  ids: action.ids,
  loading: false
});
const destroySuccess = (state, action) => _objectSpread(_objectSpread(_objectSpread({}, state), removeDestroyedEntity(state, action.ids)), {}, {
  error: null,
  pendingChanges: false,
  loading: false
});
const save = state => _objectSpread(_objectSpread({}, state), {}, {
  error: null,
  loading: true,
  pendingChanges: true
});
const saveAll = state => _objectSpread(_objectSpread({}, state), {}, {
  error: null,
  loading: true,
  pendingChanges: true
});
const saveAllSuccess = state => _objectSpread(_objectSpread({}, state), {}, {
  error: null,
  // clear the query, which determines if index view is loaded
  indexQuery: _objectSpread(_objectSpread({}, state.indexQuery), {}, {
    initialized: false
  }),
  loading: false,
  pendingChanges: false
});
const saveAllFail = (state, action) => _objectSpread(_objectSpread({}, state), {}, {
  error: action.error ? action.error : {
    message: 'failed saving'
  },
  loading: false
});

/**
 *  Save failed. No need to reset data as updating state should only be done
 *  with saveSuccess
 */
const saveFail = (state, action) => _objectSpread(_objectSpread({}, state), {}, {
  error: action.error ? action.error : {
    message: 'failed saving'
  },
  id: action.id,
  loading: false
});
const saveSuccess = (state, action) => {
  // check the data/response for an array with one element
  const newEntity = _.isArray(action.data) && action.data.length === 1 ? action.data[0] : action.data;
  newEntity.isNew = false;
  return _objectSpread(_objectSpread({}, state), {}, {
    active: newEntity,
    originalActive: clone(newEntity),
    error: null,
    // clear the query, which determines if index view is loaded
    indexQuery: _objectSpread(_objectSpread({}, state.indexQuery), {}, {
      initialized: false
    }),
    loading: false,
    pendingChanges: false,
    pendingOperation: false
  });
};
const undoChanges = state => _objectSpread(_objectSpread({}, state), {}, {
  pendingChanges: false,
  active: state.originalActive
});
export default function changeReducerFactory(entity) {
  const constants = constantsRegistry(entity);
  const detailReducer = detailReducerFactory(entity);

  // note that the returned object is intended for composition and client code will need to call
  // `applyReducers(reducers, state)` to get an expected redux reducer
  return {
    reducers: {
      [constants.CHANGE]: change,
      [constants.CHANGE_QUIETLY]: changeQuietly,
      [constants.CREATE]: create,
      [constants.CREATE_FAIL]: createFail,
      [constants.DESTROY]: destroy,
      [constants.DESTROY_SUCCESS]: destroySuccess,
      [constants.DESTROY_FAIL]: destroyFail,
      [constants.OPERATION_PENDING]: operationPending,
      [constants.OPERATION_COMPLETE]: operationComplete,
      [constants.SAVE]: save,
      [constants.SAVE_SUCCESS]: saveSuccess,
      [constants.SAVE_FAIL]: saveFail,
      [constants.SAVE_ALL]: saveAll,
      [constants.SAVE_ALL_SUCCESS]: saveAllSuccess,
      [constants.SAVE_ALL_FAIL]: saveAllFail,
      [constants.UNDO_CHANGES]: undoChanges
    },
    initState: _objectSpread(_objectSpread({}, detailReducer.initState), {}, {
      pendingChanges: false,
      pendingOperation: false,
      loading: false
    })
  };
}